import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import BoltIcon from '@mui/icons-material/Bolt';
import { useTranslation } from 'react-i18next';
import { CREDIT_COST, resolveEntitlement, websiteAuditAgentsForTier } from '@traaker/shared';
import { useCredits } from '../../contexts/CreditContext';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { formatNumber } from '../../i18n/format';

/**
 * Per-brand cost cap (every tier, pricing v2) for the Website Audit (pages crawled) and
 * Earned Media (third-party pages fetched + judged). One credit is spent per
 * page / per fetch, so the cap is both a work limit AND the credit-reserve
 * ceiling — the Trigger Lambda passes it to the agent and reserves against it.
 *
 * Renders a gear "Configurer" button that opens a popover with: an on/off
 * toggle (off = tier default, at most the 100 agent ceiling), a numeric cap (1..100),
 * a plain-language pricing explanation, and a live "≤ N crédits max / audit"
 * chip. Persists via `PATCH /brands/:id/audit-config`.
 */

type CapKind = 'website' | 'earned_media';

// Credits charged per unit (page / fetch). Website fans out to the tier's Phase-1
// agents (3, or 1 on Starter — websiteAuditAgentsForTier), each analyzing every
// page at 1 credit; earned media is a single orchestrator → 1 credit/fetch.
const EARNED_MEDIA_CREDITS_PER_FETCH = CREDIT_COST.fetch; // 1

const COPY: Record<
  CapKind,
  {
    field: keyof BrandCapFields;
    creditsPerUnit: number;
    metric: string;
  }
> = {
  website: {
    field: 'maxPagesPerWebsiteAudit',
    creditsPerUnit: 0, // tier-dependent — resolved in the component
    metric: 'pages_per_website_audit',
  },
  earned_media: {
    field: 'maxFetchPerEarnedMedia',
    creditsPerUnit: EARNED_MEDIA_CREDITS_PER_FETCH,
    metric: 'fetch_per_earned_media',
  },
};

// Mirror of the agents' hard ceiling (crawl pages_per_audit / earned-media
// TIER_BUDGETS.fetch). The server clamps to this too — keep in sync.
const AGENT_CEILING = 100;
const DEFAULT_CAP = 40;

interface BrandCapFields {
  maxPagesPerWebsiteAudit?: number;
  maxFetchPerEarnedMedia?: number;
}

interface Props {
  brandId: string;
  kind: CapKind;
  /** Current per-brand override (brand.auditConfig.maxPages…/maxFetch…), if any. */
  value: number | undefined;
  onSaved: () => Promise<void> | void;
}

export default function AuditCapConfig({ brandId, kind, value, onSaved }: Props) {
  const { t } = useTranslation();
  const { balance } = useCredits();
  const { tenant } = useAuth();
  const copy = COPY[kind];
  // Uncapped run = the default run size (registry scalar: 30 on every non-agency
  // tier, agency = the 100 agent ceiling), bounded by the agent ceiling. Mirrors
  // CostEstimate and the Trigger Lambda. Not a plan limit (pricing v2).
  const tierScalar = resolveEntitlement(copy.metric, tenant?.tier || '', {
    override: (tenant?.settings?.quotaOverrides as any)?.[copy.metric],
    packs: tenant?.settings?.activePacks?.[copy.metric],
  }).limit;
  const tierMax = tierScalar === -1 ? AGENT_CEILING : Math.min(tierScalar, AGENT_CEILING);
  const websiteAgents = websiteAuditAgentsForTier(tenant?.tier);
  const creditsPerUnit = kind === 'website' ? CREDIT_COST.page_analyzed * websiteAgents : copy.creditsPerUnit;
  const unitLabel = t(`auditCap.unitLabel.${kind}`);
  const pricingLine =
    kind === 'website'
      ? websiteAgents === 1
        ? t('auditCap.pricingLine.websiteSingle', { credits: creditsPerUnit })
        : t('auditCap.pricingLine.website', { credits: creditsPerUnit, agents: websiteAgents })
      : t('auditCap.pricingLine.earnedMedia', { credits: EARNED_MEDIA_CREDITS_PER_FETCH });
  const help = t(`auditCap.help.${kind}`);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [enabled, setEnabled] = useState<boolean>(typeof value === 'number');
  const [cap, setCap] = useState<number>(value ?? DEFAULT_CAP);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed from the brand row whenever it changes (e.g. after refreshBrands).
  useEffect(() => {
    setEnabled(typeof value === 'number');
    setCap(value ?? DEFAULT_CAP);
  }, [value]);

  const effectiveCap = enabled ? Math.max(1, Math.min(AGENT_CEILING, cap)) : tierMax;
  const maxCredits = effectiveCap * creditsPerUnit;
  const insufficient = balance ? balance.available < maxCredits : false;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // enabled → persist the clamped cap; disabled → send 0 so the server drops
      // the override (falls back to the tier default).
      const payloadValue = enabled ? Math.max(1, Math.min(AGENT_CEILING, cap)) : 0;
      await api.patch(`/brands/${brandId}/audit-config`, { [copy.field]: payloadValue });
      setAnchorEl(null);
      await onSaved();
    } catch (err: any) {
      setError(err?.message || t('auditCap.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tooltip
        title={
          typeof value === 'number'
            ? t('auditCap.buttonTooltipSet', { value, unit: unitLabel })
            : t('auditCap.buttonTooltipUnset')
        }
      >
        <Button variant="outlined" startIcon={<TuneIcon />} onClick={(e) => setAnchorEl(e.currentTarget)}>
          {typeof value === 'number' ? t('auditCap.buttonLabelSet', { value }) : t('auditCap.buttonLabelUnset')}
        </Button>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2.5, width: 340 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" fontWeight={700}>
              {t('auditCap.popoverTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {help}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={600}>
                {t('auditCap.toggleLabel')}
              </Typography>
              <Switch
                size="small"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            </Box>

            {enabled ? (
              <TextField
                size="small"
                type="number"
                label={unitLabel}
                value={cap}
                inputProps={{ min: 1, max: AGENT_CEILING }}
                onChange={(e) =>
                  setCap(Math.max(1, Math.min(AGENT_CEILING, parseInt(e.target.value, 10) || 1)))
                }
                helperText={t('auditCap.maximum', { max: AGENT_CEILING })}
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('auditCap.noCapTier', { max: tierMax, unit: unitLabel })}
              </Typography>
            )}

            {/* Pricing explanation + live max-credit estimate. */}
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                {pricingLine}
              </Typography>
              <Tooltip title={t('auditCap.chipTooltip')}>
                <Chip
                  icon={<BoltIcon fontSize="small" />}
                  size="small"
                  variant="outlined"
                  color={insufficient ? 'warning' : 'default'}
                  label={t('auditCap.chipLabel', { credits: formatNumber(maxCredits) })}
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
            </Box>

            {error && (
              <Typography color="error" variant="caption">
                {error}
              </Typography>
            )}
            {insufficient && (
              <Alert severity="warning" sx={{ py: 0 }}>
                {t('auditCap.insufficient')}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button size="small" disabled={saving} onClick={() => setAnchorEl(null)}>
                {t('auditCap.cancel')}
              </Button>
              <Button size="small" variant="contained" disabled={saving} onClick={handleSave}>
                {saving ? t('auditCap.saving') : t('auditCap.save')}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
